import { create } from "zustand";
type GenericData = Record<string, unknown>;

type CityStore = {
  cityData: { [key: string]: any } | null;
  setCityData: (data: { [key: string]: any }) => void;
};
type CountryStore = {
  countryData: { [key: string]: any } | null;
  setCountryData: (data: { [key: string]: any }) => void;
};
type StateStore = {
  stateData: { [key: string]: any } | null;
  setStateData: (data: { [key: string]: any }) => void;
};
type UserRoleStore = {
  userRoleData: any[] | null;
  setUserRoleData: (data: any[]) => void;
};
type TokenStore = {
  token: GenericData | null;
  setToken: (data: GenericData) => void;
};

type StatementOfAccountStore = {
  statementDetails: any | null;
  setStatementDetails: (data: StatementOfAccount) => void;
};

// Define types for each section
type General = {
  id: string;
  vehicleOwner: string;
  bookingType: string;
  driverFirstName: string;
  partner: string;
  date_since: string;
  date_to: string;
};

type Client = {
  clientFirstName: string;
  clientLastName: string;
  clientId: string;
  rating: string;
  clientMobile: string;
  clientEmail: string;
};

type Driver = {
  driverFirstName: string;
  driverLastName: string;
  driverId: string;
  referralKey: string;
  rating: string;
  driverMobile: string;
  driverEmail: string;
};

type Vehicle = {
  vehicleType: string;
  vehicleNo: string;
};

type Location = {
  state: string;
  city: string;
  pickup: string;
  dropOff: string;
};

type Status = {
  bookingStatus: string;
  orderTimeFrom: string;
  orderTimeTo: string;
};

type Payment = {
  paymentType: string;
  isPaid: string;
};

type FromDate = {
  from_time: string;
  from_date: string;
  booking_type: string;
};

type BookingData = {
  general: General;
  client: Client;
  driver: Driver;
  vehicle: Vehicle;
  location: Location;
  status: Status;
  payment: Payment;
  from_date: FromDate;
};

type ItineraryDetails = {
  pick_city_name: string;
  drop_city_name: string;
  distance: number;
  duration: string;
  days: number;
  pickup_date: string;
  pickup_address: string;
  drop_address: string;
  pickup_time: string;
  drop_date: string;
};

type StatementOfAccount = {
  booking_status?: string;
  agent_reference?: string;
  booked_city?: string;
  booking_reference?: string;
  passenger_first_name?: string;
  passenger_last_name?: string;
  booking_type?: string[] | [];
  vehicle_type?: string[] | [];
  from_date?: string;
  to_date?: string;
};

type BookingStore = {
  bookingData: BookingData | null;
  setBookingData: (data: BookingData) => void;
};

type ItineraryStore = {
  itineraryDetails: ItineraryDetails | null;
  setItineraryDetails: (data: ItineraryDetails) => void;
  resetItineraryDetails: () => void;
};


// Pricing data type based on your JSON structure
type PricingData = {
  sNo: number;
  category: string;
  userName: string;
  country: string;
  state: string;
  city: string;
  bookingModule: string;
  bookingType: string;
  vehicleType: string;
  currency: string;
  gradeType: string;
  amount: number;
  base: any;
  markupId: number;
  extraHour: number;
  extraKM: number;
  status: string;
  country_id: string,
  state_id: string,
  city_id: string,
  vehicle_type_id: string,
  master_booking_type_id: string,
  currency_code: string
};

type PricingStore = {
  pricingData: PricingData | null;
  setPricingData: (data: PricingData) => void;
};

type CompanyBookingData = {
  country_id: string;
  state_id: string;
  city_id: string;
  user_grade: string;
  master_booking_type_id: string;
  user_id: string;
};

type PackageModeMappingData = {
  id?: string;
  country_id?: string;
  country?: string;
  state?: string;
  city?: string;
  state_id?: string;
  city_id?: string;
  booking_type_id?: string;
  bookingType?: string;
  vehicle_type?: string;
  vehicle_type_id?: string;
  packageName?: string;
  vehicleType?: string;
  bookingMode?: string;
  createdById?: string;
  category?: string;
  bookingModeID?: string;
  status?: string;
  modifiedBy?: string;
}



type NewsletterData = {
  first_name?: string;
  last_name?: string;
  email?: string;
  mobile?: string;
  address?: string;
  city?: string;
  pincode?: string;
  status?: string;
  source?: string;
  user_type?: string;
  user_type_id?: string;
  email_subscription?: string;
  mobile_subscription?: string;
  unsubscribe_reason?: string;
  unsubscribe_reason_mobile?: string;
  company_position?: string;
  user_status?: string;
  affiliate_id?: string;
  landline?: string;
  follow_up?: string;
  id?: string;
}

type CompanyBookingStore = {
  companyBookingData: CompanyBookingData | null;
  setCompanyBookingData: (data: CompanyBookingData) => void;
  resetCompanyBookingData: () => void;
};

type PackageModeMappingStore = {
  packageModeData: PackageModeMappingData | null;
  setPackageModeData: (data: PackageModeMappingData) => void;
  resetPackageModeData: () => void;
}

type NewsletterStore = {
  newsletterData: NewsletterData | null;
  setNewsletterData: (data: NewsletterData) => void;
  resetNewsletterData: () => void;
}

type SidebarData = {
  isCollapsed: boolean;
};

type SidebarStore = {
  SidebarData: SidebarData;
  setSidebarData: (data: SidebarData) => void;
  resetSidebarData: () => void;
};

type AddedFareStore = {
  fare: any | null;
  setAddedFare: (data: any) => void;
  clearAddedFare: () => void;
};

const defaultSidebarData: SidebarData = {
  isCollapsed: true,
};

export const useCity = create<CityStore>((set) => ({
  cityData: null,
  setCityData: (data) => set({ cityData: data }),
}));

export const useCountry = create<CountryStore>((set) => ({
  countryData: null,
  setCountryData: (data) => set({ countryData: data }),
}));

export const useStateStore = create<StateStore>((set) => ({
  stateData: null,
  setStateData: (data) => set({ stateData: data }),
}));

export const useTokenStore = create<TokenStore>((set) => ({
  token: null,
  setToken: (data) => set({ token: data }),
}));

export const useUserRole = create<UserRoleStore>((set) => ({
  userRoleData: null,
  setUserRoleData: (data) => set({ userRoleData: data }),
}));

export const useBookingStore = create<BookingStore>((set) => ({
  bookingData: null,
  setBookingData: (data: BookingData) => set({ bookingData: data }),
}));

export const useItineraryDetailsStore = create<ItineraryStore>((set) => ({
  itineraryDetails: null,
  setItineraryDetails: (data) => set({ itineraryDetails: data }),
  resetItineraryDetails: () => set({ itineraryDetails: null }),
}));

export const useClientStore = create<any>((set) => ({
  itineraryDetails: null,
  setClientDetails: (data: any) => set({ clientDetails: data }),
  resetClientDetails: () => set({ clientDetails: null }),
}));

export const useStatementOfAccountStore = create<StatementOfAccountStore>(
  (set) => ({
    statementDetails: null,
    setStatementDetails: (data: StatementOfAccount) =>
      set({ statementDetails: data }),
  })
);

export const usePricingStore = create<PricingStore>((set) => ({
  pricingData: null,
  setPricingData: (data) => set({ pricingData: data }),
}));

export const useCompanyBookingStore = create<CompanyBookingStore>((set) => ({
  companyBookingData: null,
  setCompanyBookingData: (data) => set({ companyBookingData: data }),
  resetCompanyBookingData: () => set({ companyBookingData: null }),
}));

export const useSidebarToggle = create<SidebarStore>((set) => ({
  SidebarData: defaultSidebarData,
  setSidebarData: (data) => set({ SidebarData: data }),
  resetSidebarData: () => set({ SidebarData: defaultSidebarData }),
}));

export const useAddedFare = create<AddedFareStore>((set) => ({
  fare: null,
  setAddedFare: (data) => set({ fare: data }),
  clearAddedFare: () => set({ fare: null }),
}));


export const usePackageModeMappingStore = create<PackageModeMappingStore>((set) => ({
  packageModeData: {},
  setPackageModeData: (data) => set({ packageModeData: data }),
  resetPackageModeData: () => set({ packageModeData: null }),
}));


export const useNewsletterStore = create<NewsletterStore>((set) => ({
  newsletterData: {},
  setNewsletterData: (data) => set({ newsletterData: data }),
  resetNewsletterData: () => set({ newsletterData: null }),
}));